import scaredBallonPerson from "assets/illustrations/scaredBallonPerson.svg"
import waitingList from "assets/images/waitingList.svg"
import { getDateTimeFormatters } from "format/src/formatDate"
import { Button, Icon, useWindowWidth, type IconType } from "materia"
import type { PropsWithChildren, ReactNode } from "react"
import { modifiers } from "ui-tools"
import { ProductModal } from "../ProductModal"
import s from "./ProductModalEvent.module.scss"

export const ProductModalEvent = ({ children }: PropsWithChildren) => (
  <ProductModal.Side>
    <div className={s.modal}>{children}</div>
  </ProductModal.Side>
)

const Header = ({ text, color }: { text: string; color?: "red" }) => (
  <h3 className={modifiers(s, "header", color)}>{text}</h3>
)

const Ticket = ({
  isWaiting,
  attendingText,
  start,
  end,
}: {
  isWaiting: boolean
  attendingText: string
  start: string
  end: string
}) => {
  const { formatTime, formatDateTimeCustom } = getDateTimeFormatters()
  const date = formatDateTimeCustom(start, {
    weekday: "long",
    month: "short",
    day: "numeric",
  })

  const startTime = formatTime(start)
  const endTime = formatTime(end)

  return (
    <div className={modifiers(s, "ticket", { isWaiting })}>
      <div className={s.ticket__attending}>{attendingText}</div>
      <div className={s.ticket__date}>{date}</div>
      <div className={s.ticket__time}>
        {startTime} - {endTime}
      </div>
    </div>
  )
}

const DateButtons = ({ children }: PropsWithChildren) => (
  <div className={s.dateButtons}>{children}</div>
)

const DateButton = ({
  color,
  text,
  onClick,
}: {
  text: string
  color: "blue" | "white"
  onClick: () => void
}) => (
  <button onClick={onClick} className={modifiers(s, "dateButton", color)}>
    <span className={s.dateButton__label}>{text}</span>
    <Icon icon="Arrow" size="large" className={s.dateButton__arrow} />
  </button>
)

const AddToCalendar = ({
  text,
  onClick,
}: {
  text: string
  onClick: () => void
}) => (
  <button className={s.dateButtons__calendar} onClick={onClick}>
    <Icon icon="Plus" size="large" className={s.dateButtons__icon} />
    {text}
  </button>
)

const Dates = ({
  isOverflow,
  children,
}: {
  isOverflow: boolean
  children: ReactNode
}) => <ul className={modifiers(s, "dates", { isOverflow })}>{children}</ul>

const Date = ({
  isActive,
  onClick,
  month,
  day,
  time,
  isCancelled,
}: {
  month: string
  day: string
  time: string
  isActive: boolean
  isCancelled: boolean
  onClick: () => void
}) => {
  return (
    <li className={s.dateListItem}>
      <button
        onClick={onClick}
        className={modifiers(s, "date", { isActive, isCancelled })}
      >
        <div className={s.date__month}>{month}</div>
        <div className={s.date__day}>{day}</div>
        <div className={s.date__time}>{time}</div>
      </button>
    </li>
  )
}

const ToggleShowMore = ({
  buttonText,
  onClick,
}: {
  buttonText: string
  onClick: () => void
}) => {
  const { isBigWindow } = useWindowWidth()
  if (!isBigWindow) return null

  return (
    <li className={s.toggleShowMore}>
      <button className={s.toggleShowMoreButton} onClick={onClick}>
        {buttonText}
      </button>
    </li>
  )
}

const WaitingInfo = ({ text }: { text: string }) => (
  <div className={s.waitingInfo}>
    <div className={s.waitingInfo__text}>{text}</div>
    <img className={s.waitingInfo__image} src={waitingList} alt="" />
  </div>
)

const InfoList = ({
  isOpaque,
  isUrl,
  children,
  address,
}: {
  children: ReactNode
  isOpaque?: boolean
  address: string
  isUrl: boolean
}) => (
  <div className={modifiers(s, "infoList", { isOpaque })}>
    <div className={s.infoList__address}>
      <Icon icon="PinLight" />
      <div className={s.infoList__addressValue}>
        {isUrl ? (
          <a
            className={s.infoList__url}
            href={address}
            target="_blank"
            rel="noreferrer"
          >
            {address}
          </a>
        ) : (
          address
        )}
      </div>
    </div>
    <div className={s.infoList__wrapper}>{children}</div>
  </div>
)

const Info = ({
  icon,
  value,
  label,
  color,
}: {
  icon: IconType
  label: string
  color?: "red"
  value: string
}) => (
  <div className={modifiers(s, "info", color)}>
    <Icon icon={icon} />
    <div className={s.info__label}>{label}</div>
    <div className={s.info__value}>{value}</div>
  </div>
)

const Buttons = ({ children }: PropsWithChildren) => (
  <ProductModal.ActionButtons>
    <div className={s.buttons}>{children}</div>
  </ProductModal.ActionButtons>
)

const EventButton = ({
  variant,
  children,
  onClick,
  isLoading,
}: {
  variant: "attend" | "unregister" | "waiting-list" | "disabled"
  onClick: () => void
  isLoading?: boolean
  children: ReactNode
}) => {
  return (
    <>
      {variant === "attend" ? (
        <Button
          borderRadius="12"
          size="big"
          isLoading={isLoading}
          onClick={onClick}
        >
          {children}
        </Button>
      ) : (
        <button
          onClick={onClick}
          disabled={isLoading}
          className={modifiers(s, "button", variant)}
        >
          {children}
        </button>
      )}
    </>
  )
}

const Empty = ({ header }: { header: string }) => (
  <div className={s.empty}>
    <h3 className={s.empty__header}>{header}</h3>
    <img className={s.empty__image} src={scaredBallonPerson} alt="" />
  </div>
)

ProductModalEvent.Header = Header
ProductModalEvent.Ticket = Ticket
ProductModalEvent.DateButtons = DateButtons
ProductModalEvent.DateButton = DateButton
ProductModalEvent.Dates = Dates
ProductModalEvent.AddToCalendar = AddToCalendar
ProductModalEvent.Date = Date
ProductModalEvent.ToggleShowMore = ToggleShowMore
ProductModalEvent.WaitingInfo = WaitingInfo
ProductModalEvent.InfoList = InfoList
ProductModalEvent.Info = Info
ProductModalEvent.Buttons = Buttons
ProductModalEvent.EventButton = EventButton
ProductModalEvent.Empty = Empty
